// Путь: D:\QR\QR-WEB\dashboard\src\components\Admin\AdminBusinesses\AdminBusinesses.js
// Название: Admin Businesses Management
// Версия: 3.0.0
// Дата: 01.02.2026

import React, { useState, useEffect } from 'react';
import './AdminBusinesses.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://qr-money.onrender.com';

const AdminBusinesses = () => {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [subscriptionFilter, setSubscriptionFilter] = useState('all');
  const [sortField, setSortField] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Модалка деталей
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [businessDetails, setBusinessDetails] = useState(null);

  const token = localStorage.getItem('authToken');

  useEffect(() => {
    fetchBusinesses();
  }, []);

  const fetchBusinesses = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_URL}/api/admin/businesses`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error(`HTTP error: ${response.status}`);

      const data = await response.json();
      setBusinesses(data.data || []);
      console.log('✅ Бизнесы загружены:', data.data);
    } catch (err) {
      console.error('❌ Ошибка:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchBusinessDetails = async (businessId) => {
    try {
      setDetailsLoading(true);
      const response = await fetch(`${API_URL}/api/admin/businesses/${businessId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error(`HTTP error: ${response.status}`);

      const data = await response.json();
      setBusinessDetails(data.data);
      console.log('✅ Детали бизнеса:', data.data);
    } catch (err) {
      console.error('❌ Ошибка загрузки деталей:', err);
      alert('Ошибка загрузки деталей: ' + err.message);
    } finally {
      setDetailsLoading(false);
    }
  };

  // Сортировка
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  // Фильтрация и сортировка
  const getFilteredAndSortedBusinesses = () => {
    let filtered = businesses.filter(b => {
      if (statusFilter !== 'all' && b.status !== statusFilter) return false;
      if (subscriptionFilter !== 'all') {
        if (subscriptionFilter === 'none' && b.subscription_plan) return false;
        if (subscriptionFilter !== 'none' && b.subscription_status !== subscriptionFilter) return false;
      }
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        return (b.email || '').toLowerCase().includes(q) ||
               (b.company_name || '').toLowerCase().includes(q) ||
               (b.first_name || '').toLowerCase().includes(q) ||
               (b.phone || '').includes(q) ||
               String(b.id).includes(q);
      }
      return true;
    });

    // Сортировка
    filtered.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Числовые поля
      if (['wallet_balance', 'campaigns_count', 'gifts_count', 'id'].includes(sortField)) {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      }
      // Даты
      if (['created_at', 'subscription_expires'].includes(sortField)) {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  };

  const filteredBusinesses = getFilteredAndSortedBusinesses();

  // Статистика
  const stats = {
    total: businesses.length,
    active: businesses.filter(b => b.status === 'active').length,
    blocked: businesses.filter(b => b.status === 'blocked').length,
    withActiveSubscription: businesses.filter(b => b.subscription_status === 'active').length,
    trialCount: businesses.filter(b => b.subscription_plan === 'trial').length,
    monthlyCount: businesses.filter(b => b.subscription_plan === 'monthly').length,
    yearlyCount: businesses.filter(b => b.subscription_plan === 'yearly').length,
    totalWallet: businesses.reduce((sum, b) => sum + parseFloat(b.wallet_balance || 0), 0),
    totalCampaigns: businesses.reduce((sum, b) => sum + parseInt(b.campaigns_count || 0), 0),
    totalGifts: businesses.reduce((sum, b) => sum + parseInt(b.gifts_count || 0), 0),
    emailVerified: businesses.filter(b => b.email_verified).length
  };

  // Форматирование
  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatDateTime = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatMoney = (amount, currency = '€') => {
    return currency + parseFloat(amount || 0).toLocaleString('ru-RU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const getStatusBadge = (status) => {
    const map = {
      active: { label: 'Активен', className: 'badge-success' },
      blocked: { label: 'Заблокирован', className: 'badge-danger' },
      pending: { label: 'Ожидает', className: 'badge-warning' }
    };
    return map[status] || { label: status || '-', className: 'badge-secondary' };
  };

  const getSubscriptionBadge = (plan, status) => {
    if (!plan) return { label: 'Нет', className: 'badge-secondary', icon: '➖' };
    
    const planLabels = { trial: 'Trial', monthly: 'Monthly', yearly: 'Yearly' };
    const statusColors = {
      active: 'badge-success',
      cancelled: 'badge-warning',
      expired: 'badge-danger'
    };
    const statusIcons = {
      active: '✅',
      cancelled: '⏸️',
      expired: '❌'
    };
    
    return {
      label: planLabels[plan] || plan,
      className: statusColors[status] || 'badge-secondary',
      icon: statusIcons[status] || '❓',
      status: status
    };
  };

  // Проверка скоро истекает подписка (< 7 дней)
  const isSubscriptionExpiringSoon = (expiresAt) => {
    if (!expiresAt) return false;
    const days = (new Date(expiresAt) - new Date()) / (1000 * 60 * 60 * 24);
    return days > 0 && days <= 7;
  };

  // Действия
  const handleViewDetails = async (business) => {
    setSelectedBusiness(business);
    await fetchBusinessDetails(business.id);
  };

  const handleCloseModal = () => {
    setSelectedBusiness(null);
    setBusinessDetails(null);
  };

  const handleBlockBusiness = async (businessId) => {
    if (!window.confirm('Заблокировать этот бизнес?')) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${businessId}/block`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchBusinesses();
        alert('✅ Бизнес заблокирован');
      }
    } catch (err) {
      alert('❌ Ошибка: ' + err.message);
    }
  };

  const handleUnblockBusiness = async (businessId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${businessId}/unblock`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchBusinesses();
        alert('✅ Бизнес разблокирован');
      }
    } catch (err) {
      alert('❌ Ошибка: ' + err.message);
    }
  };

  // Экспорт в CSV
  const exportToCSV = () => {
    const headers = ['ID', 'Email', 'Компания', 'Телефон', 'Статус', 'Подписка', 'Статус подписки', 'Баланс EUR', 'Кампаний', 'Подарков', 'Email верифицирован', 'Дата регистрации'];
    const rows = filteredBusinesses.map(b => [
      b.id,
      b.email,
      b.company_name || b.first_name || '-',
      b.phone || '-',
      b.status,
      b.subscription_plan || '-',
      b.subscription_status || '-',
      b.wallet_balance || 0,
      b.campaigns_count || 0,
      b.gifts_count || 0,
      b.email_verified ? 'Да' : 'Нет',
      formatDate(b.created_at)
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(';')).join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `businesses_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
  };

  // Иконка сортировки
  const SortIcon = ({ field }) => {
    if (sortField !== field) return <span className="sort-icon">↕️</span>;
    return <span className="sort-icon">{sortOrder === 'asc' ? '⬆️' : '⬇️'}</span>;
  };

  if (loading) {
    return (
      <div className="admin-businesses-page">
        <div className="loading-state">⏳ Загрузка бизнесов...</div>
      </div>
    );
  }

  return (
    <div className="admin-businesses-page">
      {/* HEADER */}
      <div className="page-header">
        <div className="header-content">
          <h1>🏢 Управление бизнесами</h1>
          <p>Все зарегистрированные компании • Показано: {filteredBusinesses.length} из {businesses.length}</p>
        </div>
        <div className="header-actions">
          <button className="btn-export" onClick={exportToCSV} title="Экспорт в CSV">
            📥 Экспорт
          </button>
          <button className="btn-refresh" onClick={fetchBusinesses}>
            🔄 Обновить
          </button>
        </div>
      </div>

      {/* STATS */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">🏢</div>
          <div className="stat-content">
            <div className="stat-value">{stats.total}</div>
            <div className="stat-label">Всего бизнесов</div>
          </div>
        </div>
        <div className="stat-card green">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <div className="stat-value">{stats.active}</div>
            <div className="stat-label">Активных</div>
          </div>
        </div>
        <div className="stat-card blue">
          <div className="stat-icon">💳</div>
          <div className="stat-content">
            <div className="stat-value">{stats.withActiveSubscription}</div>
            <div className="stat-label">С активной подпиской</div>
            <div className="stat-detail">
              Trial: {stats.trialCount} | Monthly: {stats.monthlyCount} | Yearly: {stats.yearlyCount}
            </div>
          </div>
        </div>
        <div className="stat-card purple">
          <div className="stat-icon">💰</div>
          <div className="stat-content">
            <div className="stat-value">{formatMoney(stats.totalWallet)}</div>
            <div className="stat-label">Общий баланс кошельков</div>
          </div>
        </div>
        <div className="stat-card orange">
          <div className="stat-icon">📋</div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalCampaigns}</div>
            <div className="stat-label">Всего кампаний</div>
          </div>
        </div>
        <div className="stat-card cyan">
          <div className="stat-icon">🎁</div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalGifts}</div>
            <div className="stat-label">Всего подарков</div>
          </div>
        </div>
      </div>

      {/* FILTERS */}
      <div className="filters-row">
        <input
          type="text"
          className="search-input"
          placeholder="🔍 Поиск по ID, email, компании, телефону..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <select
          className="filter-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">Все статусы</option>
          <option value="active">✅ Активные ({stats.active})</option>
          <option value="blocked">🚫 Заблокированные ({stats.blocked})</option>
        </select>
        <select
          className="filter-select"
          value={subscriptionFilter}
          onChange={(e) => setSubscriptionFilter(e.target.value)}
        >
          <option value="all">Все подписки</option>
          <option value="active">💳 Активная ({stats.withActiveSubscription})</option>
          <option value="cancelled">⏸️ Отменена</option>
          <option value="expired">❌ Истекла</option>
          <option value="none">➖ Без подписки</option>
        </select>
      </div>

      {error && <div className="error-banner">⚠️ {error}</div>}

      {/* TABLE */}
      <div className="table-container">
        <table className="businesses-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('id')} className="sortable">
                ID <SortIcon field="id" />
              </th>
              <th onClick={() => handleSort('email')} className="sortable">
                Компания / Email <SortIcon field="email" />
              </th>
              <th>Телефон</th>
              <th onClick={() => handleSort('status')} className="sortable">
                Статус <SortIcon field="status" />
              </th>
              <th onClick={() => handleSort('subscription_plan')} className="sortable">
                Подписка <SortIcon field="subscription_plan" />
              </th>
              <th onClick={() => handleSort('wallet_balance')} className="sortable">
                Кошелёк EUR <SortIcon field="wallet_balance" />
              </th>
              <th onClick={() => handleSort('campaigns_count')} className="sortable">
                Кампаний <SortIcon field="campaigns_count" />
              </th>
              <th onClick={() => handleSort('gifts_count')} className="sortable">
                Подарков <SortIcon field="gifts_count" />
              </th>
              <th onClick={() => handleSort('created_at')} className="sortable">
                Регистрация <SortIcon field="created_at" />
              </th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {filteredBusinesses.length > 0 ? (
              filteredBusinesses.map((b) => {
                const statusBadge = getStatusBadge(b.status);
                const subBadge = getSubscriptionBadge(b.subscription_plan, b.subscription_status);
                const expiringSoon = isSubscriptionExpiringSoon(b.subscription_expires);
                
                return (
                  <tr key={b.id} className={b.status === 'blocked' ? 'row-blocked' : ''}>
                    <td className="id-cell">#{b.id}</td>
                    <td className="company-cell">
                      <div className="company-name">
                        {b.email_verified && <span className="verified-icon" title="Email подтверждён">✓</span>}
                        {b.company_name || b.first_name || b.last_name || 'Не указано'}
                      </div>
                      <div className="company-email">{b.email}</div>
                    </td>
                    <td className="phone-cell">{b.phone || '-'}</td>
                    <td>
                      <span className={`badge ${statusBadge.className}`}>{statusBadge.label}</span>
                    </td>
                    <td className="subscription-cell">
                      <span className={`badge ${subBadge.className}`}>
                        {subBadge.icon} {subBadge.label}
                      </span>
                      {b.subscription_expires && (
                        <div className={`sub-expires ${expiringSoon ? 'expiring-soon' : ''}`}>
                          {expiringSoon && '⚠️ '}до {formatDate(b.subscription_expires)}
                        </div>
                      )}
                    </td>
                    <td className="money-cell">
                      <strong>{formatMoney(b.wallet_balance)}</strong>
                    </td>
                    <td className="number-cell">{b.campaigns_count || 0}</td>
                    <td className="number-cell">{b.gifts_count || 0}</td>
                    <td className="date-cell">{formatDate(b.created_at)}</td>
                    <td className="actions-cell">
                      <button
                        className="btn-action btn-view"
                        onClick={() => handleViewDetails(b)}
                        title="Подробнее"
                      >
                        👁️
                      </button>
                      {b.status === 'active' ? (
                        <button
                          className="btn-action btn-block"
                          onClick={() => handleBlockBusiness(b.id)}
                          title="Заблокировать"
                        >
                          🚫
                        </button>
                      ) : b.status === 'blocked' ? (
                        <button
                          className="btn-action btn-unblock"
                          onClick={() => handleUnblockBusiness(b.id)}
                          title="Разблокировать"
                        >
                          🔓
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="10" className="no-data">
                  {searchQuery || statusFilter !== 'all' || subscriptionFilter !== 'all'
                    ? 'Бизнесы не найдены по заданным фильтрам'
                    : 'Нет зарегистрированных бизнесов'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* MODAL - Детали бизнеса */}
      {selectedBusiness && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🏢 Детали бизнеса #{selectedBusiness.id}</h2>
              <button className="modal-close" onClick={handleCloseModal}>✕</button>
            </div>
            
            {detailsLoading ? (
              <div className="modal-loading">⏳ Загрузка деталей...</div>
            ) : businessDetails ? (
              <div className="modal-body">
                {/* Основная информация */}
                <div className="detail-section">
                  <h3>👤 Основная информация</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">ID:</span>
                      <span className="detail-value">#{businessDetails.id}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Email:</span>
                      <span className="detail-value">
                        {businessDetails.email}
                        {businessDetails.email_verified && <span className="verified-badge">✓ Подтверждён</span>}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Компания:</span>
                      <span className="detail-value">{businessDetails.company_name || '-'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Имя:</span>
                      <span className="detail-value">{businessDetails.first_name || '-'} {businessDetails.last_name || ''}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Телефон:</span>
                      <span className="detail-value">{businessDetails.phone || '-'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Страна:</span>
                      <span className="detail-value">{businessDetails.country || '-'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Статус:</span>
                      <span className={`badge ${getStatusBadge(businessDetails.status).className}`}>
                        {getStatusBadge(businessDetails.status).label}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Регистрация:</span>
                      <span className="detail-value">{formatDateTime(businessDetails.created_at)}</span>
                    </div>
                  </div>
                </div>

                {/* Подписка */}
                <div className="detail-section">
                  <h3>💳 Подписка</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">План:</span>
                      <span className="detail-value">{businessDetails.plan_code || '-'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Статус:</span>
                      <span className={`badge ${getSubscriptionBadge(businessDetails.plan_code, businessDetails.subscription_status).className}`}>
                        {businessDetails.subscription_status || '-'}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Начало:</span>
                      <span className="detail-value">{formatDate(businessDetails.starts_at)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Истекает:</span>
                      <span className="detail-value">{formatDate(businessDetails.expires_at)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Авто-продление:</span>
                      <span className="detail-value">{businessDetails.auto_renew ? '✅ Да' : '❌ Нет'}</span>
                    </div>
                  </div>
                </div>

                {/* Кошелёк */}
                <div className="detail-section">
                  <h3>💰 Кошелёк (EUR)</h3>
                  <div className="detail-grid wallet-grid">
                    <div className="wallet-card">
                      <div className="wallet-label">Депозит</div>
                      <div className="wallet-value">{formatMoney(businessDetails.deposit_balance)}</div>
                    </div>
                    <div className="wallet-card green">
                      <div className="wallet-label">Доступно</div>
                      <div className="wallet-value">{formatMoney(businessDetails.available_balance)}</div>
                    </div>
                    <div className="wallet-card orange">
                      <div className="wallet-label">Промо резерв</div>
                      <div className="wallet-value">{formatMoney(businessDetails.promo_reserve)}</div>
                    </div>
                    <div className="wallet-card purple">
                      <div className="wallet-label">Комиссии резерв</div>
                      <div className="wallet-value">{formatMoney(businessDetails.fees_reserve)}</div>
                    </div>
                  </div>
                </div>

                {/* Последние кампании */}
                {businessDetails.recent_campaigns && businessDetails.recent_campaigns.length > 0 && (
                  <div className="detail-section">
                    <h3>📋 Последние кампании ({businessDetails.recent_campaigns.length})</h3>
                    <div className="campaigns-list">
                      {businessDetails.recent_campaigns.map((camp) => (
                        <div key={camp.id} className="campaign-item">
                          <div className="campaign-info">
                            <span className="campaign-name">#{camp.id} {camp.name}</span>
                            <span className={`badge badge-sm ${camp.status === 'completed' ? 'badge-success' : camp.status === 'active' ? 'badge-primary' : 'badge-secondary'}`}>
                              {camp.status}
                            </span>
                          </div>
                          <div className="campaign-meta">
                            <span className="campaign-type">{camp.type}</span>
                            <span className="campaign-date">{formatDate(camp.created_at)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Верификация */}
                <div className="detail-section">
                  <h3>🔐 Верификация</h3>
                  <div className="verification-grid">
                    <div className={`verification-item ${businessDetails.email_verified ? 'verified' : ''}`}>
                      {businessDetails.email_verified ? '✅' : '❌'} Email
                    </div>
                    <div className={`verification-item ${businessDetails.phone_verified ? 'verified' : ''}`}>
                      {businessDetails.phone_verified ? '✅' : '❌'} Телефон
                    </div>
                    <div className={`verification-item ${businessDetails.document_verified ? 'verified' : ''}`}>
                      {businessDetails.document_verified ? '✅' : '❌'} Документы
                    </div>
                    <div className={`verification-item ${businessDetails.profile_completed ? 'verified' : ''}`}>
                      {businessDetails.profile_completed ? '✅' : '❌'} Профиль заполнен
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="modal-error">❌ Не удалось загрузить данные</div>
            )}

            <div className="modal-footer">
              {businessDetails && (
                <>
                  {businessDetails.status === 'active' ? (
                    <button 
                      className="btn btn-danger"
                      onClick={() => { handleBlockBusiness(businessDetails.id); handleCloseModal(); }}
                    >
                      🚫 Заблокировать
                    </button>
                  ) : businessDetails.status === 'blocked' ? (
                    <button 
                      className="btn btn-success"
                      onClick={() => { handleUnblockBusiness(businessDetails.id); handleCloseModal(); }}
                    >
                      🔓 Разблокировать
                    </button>
                  ) : null}
                </>
              )}
              <button className="btn btn-secondary" onClick={handleCloseModal}>
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBusinesses;